import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { api, LinkedInStats, LinkedInSyncJob } from '../../../services/api'

/**
 * Headline counts plus the state of each scheduled job.
 *
 * The ledger behind this was written on every run and read by nothing, so the
 * daily scrape failed for two months without a single visible signal. A job is
 * only "healthy" when its last run succeeded AND happened recently: one that
 * silently stopped being scheduled looks perfect by status alone.
 */

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
`

const Kpi = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.75rem 1rem;
`

const KpiValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
`

const KpiLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.7;
`

const Jobs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`

type Tone = 'ok' | 'idle' | 'warn' | 'bad'

const TONES: Record<Tone, { bg: string; fg: string; icon: string }> = {
    ok: { bg: '#E8F5E9', fg: '#2E7D32', icon: '\u2713' },
    idle: { bg: '#ECEFF1', fg: '#546E7A', icon: '\u25CB' },
    warn: { bg: '#FFF8E1', fg: '#8D6E00', icon: '\u26A0' },
    bad: { bg: '#FDECEA', fg: '#B3261E', icon: '\u26A0' },
}

const JobChip = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  padding: 0.3rem 0.7rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ $tone }) => TONES[$tone].bg};
  color: ${({ $tone }) => TONES[$tone].fg};
  font-weight: 600;
`

const JOB_LABELS: Record<string, string> = {
    scrape: 'Ingesta de DMs',
    ai: 'Agente de IA',
    repo: 'Agente de repos',
    cluster: 'Clusteres',
    cluster_recompute: 'Reconstrucción de clusteres',
}

/**
 * A job is not "failed" just because it is not "ok".
 *
 * Everything that was not literally `ok` used to render as "falló", so a fresh
 * deployment — where nothing has run yet and the backend reports `never` —
 * announced four red failures on a page that had simply never been used. A run
 * in flight said the same thing, and so did one deliberately skipped for want of
 * a session. Each of those calls for a different answer, and only one of them is
 * bad news.
 */
function describe(job: LinkedInSyncJob): { text: string; tone: Tone } {
    const hours = job.hours_since_last_run
    const ago =
        hours === null || hours === undefined
            ? null
            : hours < 1
              ? 'hace minutos'
              : hours < 24
                ? `hace ${Math.round(hours)} h`
                : `hace ${Math.round(hours / 24)} d`

    if (job.status === 'never' || ago === null) return { text: 'sin ejecutar aún', tone: 'idle' }
    if (job.status === 'running') return { text: 'en curso', tone: 'idle' }
    // Skipped is "could not run" — throttled, or no session — which is worth a
    // warning but is not the same as a crash.
    if (job.status === 'skipped') return { text: `omitida ${ago}`, tone: 'warn' }
    if (job.status === 'partial') return { text: `parcial ${ago}`, tone: 'warn' }
    if (job.status !== 'ok') return { text: `falló ${ago}`, tone: 'bad' }
    if (job.stale) return { text: `sin ejecutarse desde ${ago}`, tone: 'warn' }
    return { text: ago, tone: 'ok' }
}

export const PipelineHealthBar: React.FC = () => {
    const [stats, setStats] = useState<LinkedInStats | null>(null)
    const [jobs, setJobs] = useState<LinkedInSyncJob[]>([])

    useEffect(() => {
        let cancelled = false
        Promise.all([api.linkedin.getStats(), api.linkedin.getSyncStatus()])
            .then(([s, h]) => {
                if (cancelled) return
                setStats(s.data)
                setJobs(h.data.jobs)
            })
            // A failure here must not take the feed down with it: this is context,
            // not the content people came for.
            .catch(() => undefined)
        return () => {
            cancelled = true
        }
    }, [])

    if (!stats) return null

    return (
        <Wrap>
            <Kpis>
                <Kpi>
                    <KpiValue>{stats.posts}</KpiValue>
                    <KpiLabel>Publicaciones</KpiLabel>
                </Kpi>
                <Kpi>
                    <KpiValue>{stats.repos}</KpiValue>
                    <KpiLabel>Repositorios</KpiLabel>
                </Kpi>
                <Kpi>
                    <KpiValue>{stats.papers}</KpiValue>
                    <KpiLabel>Papers</KpiLabel>
                </Kpi>
                <Kpi>
                    <KpiValue>{stats.commits}</KpiValue>
                    <KpiLabel>Commits</KpiLabel>
                </Kpi>
                <Kpi>
                    <KpiValue>{stats.repos_trending}</KpiValue>
                    <KpiLabel>En tendencia</KpiLabel>
                </Kpi>
                <Kpi>
                    <KpiValue>{stats.open_assignments}</KpiValue>
                    <KpiLabel>Tareas abiertas</KpiLabel>
                </Kpi>
            </Kpis>

            {jobs.length > 0 && (
                <Jobs>
                    {jobs.map((job) => {
                        const { text, tone } = describe(job)
                        return (
                            <JobChip key={job.job} $tone={tone} title={job.error || undefined}>
                                {TONES[tone].icon} {JOB_LABELS[job.job] || job.job}: {text}
                            </JobChip>
                        )
                    })}
                </Jobs>
            )}
        </Wrap>
    )
}

export default PipelineHealthBar
